import type { StaffProfile, Training, TrainingAttendance } from "../services/staffService";
import { localToday } from "./leaveFilters";

export const TRAINING_TYPES: [string, string][] = [
  ["orientation", "Orientation"],
  ["professional", "Professional development"],
  ["safety", "Safety"],
  ["technology", "Technology"],
  ["leadership", "Leadership"],
  ["other", "Other"],
];

export const trainingTypeLabel = (type: string) =>
  TRAINING_TYPES.find(([value]) => value === type)?.[1] ?? type.replace(/_/g, " ");

export type TrainingPhase = "upcoming" | "running" | "completed";

export const TRAINING_PHASE_TABS: [string, string][] = [
  ["", "All"],
  ["upcoming", "Upcoming"],
  ["running", "In progress"],
  ["completed", "Completed"],
];

export const TRAINING_PHASE_DOTS: Record<TrainingPhase, string> = {
  upcoming: "bg-blue-500",
  running: "bg-amber-500",
  completed: "bg-green-500",
};

export function trainingPhase(training: Training, today = localToday()): TrainingPhase {
  if (training.start_date > today) return "upcoming";
  if (training.end_date < today) return "completed";
  return "running";
}

export type TrainingFilters = {
  search: string;
  phase: string;
  type: string;
  mandatory: "" | "yes" | "no";
};

export const EMPTY_TRAINING_FILTERS: TrainingFilters = { search: "", phase: "", type: "", mandatory: "" };

export function activeTrainingFilterCount(filters: TrainingFilters) {
  return Object.values(filters).filter((value) => value.trim()).length;
}

export function trainingPhaseCounts(sessions: Training[], today = localToday()) {
  const counts: Record<string, number> = { "": sessions.length };
  for (const session of sessions) {
    const phase = trainingPhase(session, today);
    counts[phase] = (counts[phase] ?? 0) + 1;
  }
  return counts;
}

/** Sessions matching the filters: upcoming and running soonest first, then past ones newest first. */
export function filterTraining(
  sessions: Training[],
  filters: TrainingFilters,
  { ignorePhase = false, today = localToday() } = {},
) {
  const term = filters.search.trim().toLowerCase();
  const rank = { running: 0, upcoming: 1, completed: 2 };
  return sessions
    .filter(
      (session) =>
        (ignorePhase || !filters.phase || trainingPhase(session, today) === filters.phase) &&
        (!term ||
          [session.name, session.trainer ?? "", session.venue ?? "", session.organizer_name ?? ""].some((value) =>
            value.toLowerCase().includes(term),
          )) &&
        (!filters.type || session.training_type === filters.type) &&
        (!filters.mandatory || session.is_mandatory === (filters.mandatory === "yes")),
    )
    .sort((a, b) => {
      const phaseA = trainingPhase(a, today);
      const phaseB = trainingPhase(b, today);
      if (phaseA !== phaseB) return rank[phaseA] - rank[phaseB];
      return phaseA === "completed"
        ? b.start_date.localeCompare(a.start_date)
        : a.start_date.localeCompare(b.start_date);
    });
}

/** Attendance and results for one session. */
export function sessionStats(trainingId: number, records: TrainingAttendance[]) {
  const mine = records.filter((record) => record.training === trainingId);
  return {
    records: mine,
    attended: mine.filter((record) => record.attended).length,
    passed: mine.filter((record) => record.passed).length,
  };
}

/** Working staff with no result yet for a session: who still has to do a mandatory one. */
export function outstandingStaff(trainingId: number, records: TrainingAttendance[], profiles: StaffProfile[]) {
  const done = new Set(records.filter((record) => record.training === trainingId && record.attended).map((record) => record.staff));
  return profiles.filter(
    (profile) => ["active", "probation"].includes(profile.employment_status) && !done.has(profile.id),
  );
}

export const COMPLETION_RESULT_TABS: [string, string][] = [
  ["", "All"],
  ["passed", "Passed"],
  ["not_passed", "Not passed"],
  ["absent", "Absent"],
];

const completionResult = (record: TrainingAttendance) =>
  !record.attended ? "absent" : record.passed ? "passed" : "not_passed";

export function completionCounts(records: TrainingAttendance[]) {
  const counts: Record<string, number> = { "": records.length };
  for (const record of records) {
    const result = completionResult(record);
    counts[result] = (counts[result] ?? 0) + 1;
  }
  return counts;
}

/** Results matching a search and result, newest completion first. */
export function filterCompletions(records: TrainingAttendance[], search: string, result = "") {
  const term = search.trim().toLowerCase();
  return records
    .filter(
      (record) =>
        (!result || completionResult(record) === result) &&
        (!term || [record.staff_name, record.training_name].some((value) => value.toLowerCase().includes(term))),
    )
    .sort(
      (a, b) =>
        (b.completion_date ?? "").localeCompare(a.completion_date ?? "") ||
        a.staff_name.localeCompare(b.staff_name),
    );
}
